/*
 Style guide for module:
 The module is functional inspired
 Utility functions that are not specific to this should follow referencial transperency, as they will eventually be moved out to a seperate utilities.js file.

 Referencial transperency is not nessecary for functions specific to this module, aslong their used variables are defined from module, execept for the few instances where it makes sense to access a prop in the window object. 
 

 Side effects should be kept to a minimal.
 

 Events should be organized as intent events and result events,
 IE: WISHLIST:REMOVE_REQUEST and then WISHLIST:REMOVE_SUCCESS.
*/

import showErrorPopup from '../assets/show-error-popup.js'

// constants
const SELECTOR_ADD_TO_WISHLIST_BUTTON = 'add-to-wishlist'
const SELECTOR_WISHLIST_LINK = 'wishlist-link'
const SELECTOR_WISHLIST_ADDED_POPUP = 'wishlist-added-popup'
const SELECTOR_WISHLIST_REMOVED_POPUP = 'wishlist-removed-popup'
const SELECTOR_WISHLIST_GRID = 'wishlist-grid'
const SELECTOR_WISHLIST_CARD_REMOVE_BUTTON = 'wishlist-card-remove-button'
const SELECTOR_WISHLIST_NOTIFICATION_IMAGE = 'wishlist-notification-image'
const SELECTOR_WISHLIST_NOTIFICATION_TITLE = 'wishlist-notification-title'
const TEXT_SCREEN_READER_SELECTOR = 'screen-reader-text'
const TEXT_SCREEN_READER_ADD = 'Add to wishlist'
const TEXT_SCREEN_READER_REMOVE = 'Remove from wishlist'
const TEXT_VERMIL = 'Vermeil'
const TEXT_SILVER = 'Silver'
const TEXT_GOLD_14K = '14k Gold'
const TEXT_GOLD_18K = '18k Gold'
const TEXT_PLATINUM = 'Platinum'
const CLASS_HIDDEN = 'u-hidden'
const CLASS_BLOW_UP_XL = 'u-object-contain-blown-up--xl'
const CLASS_BLOW_UP_XXL = 'u-object-contain-blown-up--xxl'
const CLASS_BLOW_UP_XXXL = 'u-object-contain-blown-up--xxxl'
const CLASS_PRODUCT_TILE_GRID = 'o-product-tile-grid'
const CLASS_PRODUCT_TILE_MAIN_IMAGE_CONTAINER = 'm-product-tile__visual-image p-simple-toggle p-simple-toggle--fade'
const CLASS_PRODUCT_CARD_DELETE = 'm-product-tile__card-delete'
const DATA_COMPONENT_PREFIX = 'data-component'
const ATTRIBUTE_PRODUCT_TILE_IMAGE = 'data-product-tile-image'
const STATE_ADDED = 'added'
const STATE_NOT_ADDED = 'not-added'
const URL_WISHLIST = '/pages/wishlist'
const URL_ROOT = 'https://auratenewyork.com/'
const ARIA_REMOVE_FROM_WISHLIST = 'Remove from wishlist'
const TIMEOUT_POPUP = 3000
const ID_MOTHERS_RING_PRODUCT = 8152969216172

const state = {
    swat: null,
    firstList: null,
    productsInWishlist: []
}

const events = {
    WISHLIST_LOAD: 'WISHLIST:LOAD',
    WISHLIST_INIT: 'WISHLIST:INIT',
    WISHLIST_LISTS_FETCH_REQUEST: 'WISHLIST:LISTS_FETCH_REQUEST',
    WISHLIST_LISTS_FETCH_SUCCESS: 'WISHLIST:LISTS_FETCH_SUCCESS',
    WISHLIST_ADD_REQUEST: 'WISHLIST:ADD_REQUEST',
    WISHLIST_ITEM_ADD_SUCCESS: 'WISHLIST:ITEM_ADD_SUCCESS',
    WISHLIST_ITEM_REMOVE_SUCCESS: 'WISHLIST:ITEM_REMOVE_SUCCESS',
    WISHLIST_REMOVE_REQUEST: 'WISHLIST:REMOVE_REQUEST',
    WISHLIST_REMOVE_SUCCESS: 'WISHLIST:REMOVE_SUCCESS',
    WISHLIST_GRID: {
        UPDATE: 'WISHLIST_GRID:UPDATE'
    },
    VARIANT: {
        CHANGE: 'VARIANT:CHANGE'
    }
}

const HOOK_CREATE = 'oncreate'
const HOOK_UPDATE = 'onupdate'
const HOOK_REMOVE = 'onremove'

const SVG_NS = 'http://www.w3.org/2000/svg'
const XLINK_NS = 'http://www.w3.org/1999/xlink'

const colorsRegex = [
    /yellow/i,
    /rose/i,
    /white/i,
]

/** @param {Number} globalSaleFirstTierMinimum - Global Sale First Tier Minimum */
const globalSaleFirstTierMinimum = window.globalSaleFirstTierMinimum
const globalSaleFirstTierLabel = window.globalSaleFirstTierLabel
const globalSaleFirstTierRatio = window.globalSaleFirstTierRatio

const globalSaleSecondTierMinimum = window.globalSaleSecondTierMinimum
const globalSaleSecondTierLabel = window.globalSaleSecondTierLabel
const globalSaleSecondTierRatio = window.globalSaleSecondTierRatio

const SALES_TIERS = [
    { min: globalSaleFirstTierMinimum, label: globalSaleFirstTierLabel, ratio: globalSaleFirstTierRatio },
    { min: globalSaleSecondTierMinimum, label: globalSaleSecondTierLabel, ratio: globalSaleSecondTierRatio }
]

// Utils
/**
 * Adds a CSS class to an element
 * @param {HTMLElement} el - The element to add the class to
 * @param {string} className - The CSS class name to add
 * @returns {HTMLElement} The element with the class added
 */
function addClass(el, className) {
    el.classList.add(className)
    return el
}

/**
 * Gets a component element by data-component attribute
 * @param {string} string - The component identifier
 * @returns {HTMLElement} The component element or a new div if not found
*/
function component(string) {
    return document.querySelector(`[data-component-${string}]`) || document.createElement('div')
}

/**
 * Gets a child component element within a parent element
 * @param {HTMLElement} el - The parent element
 * @param {string} string - The component identifier
 * @returns {HTMLElement | null} The component element or null if not found
*/
function componentOf(el, string) {
    return el.querySelector(`[data-component-${string}]`)
}

/**
 * Gets all component elements matching a data-component attribute
 * @param {string} string - The component identifier
 * @returns {NodeListOf<HTMLElement>} NodeList of matching component elements
*/
function components(string) {
    return document.querySelectorAll(`[data-component-${string}]`)
}

/**
 * Computes the price view model for a product variant with sale logic
 * Handles special pricing for earrings (pair vs single) and applies sale discounts
 * @param {Object} params - The parameters object
 * @param {Product} params.product - The product object
 * @param {Variant} params.variant - The variant object
 * @param {boolean} [params.showSaleCopy=false] - Whether to show sale copy
 * @param {number} [params.saleRatio=0] - The sale ratio to apply (e.g., 0.8 for 20% off)
 * @param {string} [params.saleLabel=''] - The sale label text to display
 * @returns {PriceViewModel} Price view model with mode, prices, and display options
 */
function computePriceView({
    product,
    variant,
    showSaleCopy = false,
    saleRatio = 0,
    saleLabel = ''
}) {
    const hasTag = (tags, t) => tags.includes(t);

    const getPercentageDifference = (originalPrice, compareAtPrice) => {
        const result = Math.round(((compareAtPrice - originalPrice) / compareAtPrice) * 100)
        return result
    }

    const priceCents = variant?.price ?? 0
    const compareCents = variant?.compare_at_price ?? 0
    const hasCompareAt = compareCents !== 0

    const type = product?.type || ''
    const productTags = product?.tags || []
    const isOnlyPricedAsSingle = hasTag(productTags, 'only-priced-as-single')

    const appendPair = (txt) => `${txt} pair`

    /** @type {PriceViewModel} */
    const out = {
        mode: 'nowrap',
        striked: null,
        discounted: null,
        text: null,
        doubled: undefined,
        variantUsed: variant,
        saleLabel: showSaleCopy ? saleLabel : null
    };

    // ---------- EARRINGS BRANCH ----------
    if (type === 'Earrings' || type === 'Earring') {
        if (isOnlyPricedAsSingle) {
            if (hasCompareAt) {
                const compareDiference = compareCents - priceCents
                const compareRatio = compareDiference / compareCents
                const factor = 1.0 - compareRatio
                const newPrice = Math.round(compareCents * factor)

                out.mode = 'strikeDiscount'
                out.striked = formatMoney(compareCents)
                out.discounted = formatMoney(newPrice)
                out.saleLabel = getPercentageDifference(newPrice, compareCents) + '% Off'
            } else if (showSaleCopy) {
                const newPrice = Math.round(priceCents * saleRatio)
                out.mode = 'strikeDiscount'
                out.striked = formatMoney(priceCents)
                out.discounted = formatMoney(newPrice)
            } else {
                out.mode = 'nowrap'
                out.text = formatMoney(priceCents)
            }
        } else {
            if (hasCompareAt) {
                const compareDiference = compareCents - priceCents
                const compareRatio = compareDiference / compareCents
                const factor = 1.0 - compareRatio
                const newPrice = Math.round(compareCents * factor)

                out.mode = 'strikeDiscount'
                out.striked = appendPair(formatMoney(compareCents))
                out.discounted = appendPair(formatMoney(newPrice))
                out.saleLabel = getPercentageDifference(newPrice, compareCents) + '% Off'
            } else if (showSaleCopy) {
                const newPrice = Math.round(priceCents * saleRatio)

                out.mode = 'strikeDiscount'
                out.striked = appendPair(formatMoney(priceCents))
                out.discounted = appendPair(formatMoney(newPrice))
            } else {
                out.mode = 'nowrap'
                out.text = appendPair(formatMoney(priceCents))
                out.doubled = true
            }
        }

        // ---------- NON-EARRINGS BRANCH ----------
    } else {
        if (hasCompareAt) {
            out.mode = 'strikeDiscount';
            out.striked = formatMoney(compareCents);
            out.discounted = formatMoney(priceCents);
        } else if (showSaleCopy) {
            const newPrice = Math.round(priceCents * saleRatio);
            out.mode = 'strikeDiscount';
            out.striked = formatMoney(priceCents);
            out.discounted = formatMoney(newPrice);
        } else {
            out.mode = 'nowrap';
            out.text = formatMoney(priceCents);
        }
    }

    return out;
}

/**
 * Creates a DOM node from a virtual node
 * @param {VNode | string | number} v - The virtual node or primitive value
 * @param {boolean} [inSvg=false] - Whether we're inside an SVG context
 * @returns {Node} The created DOM node (Element or Text node)
 */
function createDom(v, inSvg = false) {
    if (isText(v)) return document.createTextNode(String(v));

    const isSvg = inSvg || v.type === 'svg';
    /** @type {Element} */
    const el = isSvg
        ? document.createElementNS(SVG_NS, v.type)
        : document.createElement(v.type);

    setProps(el, v.props, {}, isSvg);

    for (const c of v.children || []) el.appendChild(createDom(c, isSvg));
    runHook(HOOK_CREATE, v, el, null);
    return el;
}

/** curried function to add an event listener with delegation
 * @param {string} eventType - the event type to listen for
 * @returns {function(string): function(function(EventTarget, Event): void): void} - a curried function to add the event listener
 **/
const curriedDelegateListen = (eventType) => (queryString) => (callback) => {
    document.addEventListener(eventType, (event) => {
        /** @type {EventTarget|null} */
        const eventTarget = event.target ?? null

        // Type guard: only Elements have classList, id, and attributes
        if (!(eventTarget instanceof Element)) return;

        /** @type {Attr[]} */
        const targetAttributes = Array.from(eventTarget.attributes) ?? []

        if (
            eventTarget.classList?.contains(queryString) ||
            eventTarget.id == queryString ||
            Array.from(targetAttributes)
                .map((attribute) => attribute.localName)
                .find((string) => string.includes(queryString))
        ) {
            callback(eventTarget, event)
        }
        return eventTarget
    })
}

/**
 * Curried function to add an event listener
 * @param {string} eventType - the event type to listen for
 * @returns {function(function(HTMLElement, Event, any): void): function(HTMLElement, any): HTMLElement} - a curried function to add the event listener
 */
const curriedListen = (eventType) => (callback) => (el, variables) => {

    document.addEventListener(eventType, (event) => {
        callback(el, event, variables)
    })
    return el
}

/**
 * Disables a button element
 * @param {HTMLButtonElement} button - The button to disable
 * @returns {HTMLButtonElement} The disabled button
 */
function disableButton(button) {
    button.disabled = true
    return button
}

/**
 * Emits a grid update custom event
 * @param {HTMLElement} el - the element to include in the event detail
 * @param {Object} params - parameters object
 * @param {VNode} params.vnode - the virtual node
 * @returns {void}
 */
const emitGridUpdate = (el, { vnode }) => {
    document.dispatchEvent(new CustomEvent(events.WISHLIST_GRID.UPDATE, {
        detail: { el }
    }))
}

/**
 * Emits a custom event
 * @param {string} eventName - the name of the event
 * @param {any} data - the data to include in the event detail
 * @returns {void}
 */
const emit = (eventName, data) => {
    document.dispatchEvent(new CustomEvent(eventName, { detail: data }))
}

/**
 * Enables a button element
 * @param {HTMLButtonElement} button - The button to enable
 * @returns {HTMLButtonElement} The enabled button
 */
function enableButton(button) {
    button.disabled = false
    return button
}

/**
 * Checks if a product exists in a Swym wishlist
 * @param {SwymList} list - The Swym wishlist to check
 * @param {WishlistProductInfo} productInfo - The product info to look for
 * @returns {boolean} True if the product exists in the list
 */
function existsInList(list, productInfo) {
    const content = list.listcontents
    const contentIds = content.map(item => item.epi)
    return contentIds.includes(productInfo.epi)
}

/**
 * Fetches product information from a URL
 * @param {string} du - The URL to fetch product information from
 * @returns {Promise<Product>} The product information
 */
async function fetchProductInfo(du) {
    try {
        const productUrl = `${du}.js`
        const response = await fetch(productUrl)
        const productData = await response.json()
        return productData
    } catch (error) {
        console.error(error)
        showErrorPopup(JSON.stringify(error))
    }
}

/**
 * Formats a price in cents to a currency string
 * @param {number|string} cents - The price in cents (can be number or string)
 * @returns {string} Formatted money string with currency symbol
 */
function formatMoney(cents) {
    /**
     * Formats a number with delimiters
     * @param {number} number - The number to format
     * @param {number} [precision=2] - Number of decimal places
     * @param {string} [thousands=','] - Thousands separator
     * @param {string} [decimal='.'] - Decimal separator
     * @returns {string} Formatted number string
     */
    function formatWithDelimiters(number, precision = 2, thousands = ',', decimal = '.') {
        if (isNaN(number) || number == null) {
            return '0'
        }

        const formattedNumber = (number / 100.0).toFixed(precision)

        var parts = formattedNumber.split('.')
        var dollarsAmount = parts[0].replace(
            /(\d)(?=(\d\d\d)+(?!\d))/g,
            '$1' + thousands
        )
        var centsAmount = parts[1] ? decimal + parts[1] : ''

        return dollarsAmount + centsAmount
    }

    const getSymbol = (string) => {
        switch (string) {
            case 'AFN':
                return '؋'
            case 'ALL':
                return 'L'
            case 'DZD':
                return 'د.ج'
            case 'EUR':
                return '€'
            case 'AOA':
                return 'Kz'
            case 'XCD':
                return '$'
            case 'ARS':
                return '$'
            case 'AMD':
                return 'դր.'
            case 'AWG':
                return 'ƒ'
            case 'AUD':
                return '$'
            case 'AZN':
                return '₼'
            case 'BSD':
                return '$'
            case 'USD':
                return '$'
            case 'BDT':
                return '৳'
            case 'BBD':
                return '$'
            case 'BZD':
                return '$'
            case 'XOF':
                return 'Fr'
            case 'BOB':
                return 'Bs.'
            case 'BAM':
                return 'КМ'
            case 'BWP':
                return 'P'
            case 'BND':
                return '$'
            case 'BGN':
                return 'лв.'
            case 'BIF':
                return 'Fr'
            case 'KHR':
                return '៛'
            case 'XAF':
                return 'Fr'
            case 'CAD':
                return '$'
            case 'CVE':
                return '$'
            case 'KYD':
                return '$'
            case 'CLP':
                return '$'
            case 'CNY':
                return '¥'
            case 'COP':
                return '$'
            case 'KMF':
                return 'Fr'
            case 'CDF':
                return 'Fr'
            case 'NZD':
                return '$'
            case 'CRC':
                return '₡'
            case 'CZK':
                return 'Kč'
            case 'DKK':
                return 'kr.'
            case 'DJF':
                return 'Fdj'
            case 'DOP':
                return '$'
            case 'EGP':
                return 'ج.م'
            case 'ETB':
                return 'Br'
            case 'FKP':
                return '£'
            case 'FJD':
                return '$'
            case 'GMD':
                return 'D'
            case 'GEL':
                return 'ლ'
            case 'GIP':
                return '£'
            case 'GTQ':
                return 'Q'
            case 'GYD':
                return '$'
            case 'HTG':
                return 'G'
            case 'HNL':
                return 'L'
            case 'HKD':
                return '$'
            case 'HUF':
                return 'Ft'
            case 'ISK':
                return 'kr'
            case 'IDR':
                return 'Rp'
            case 'ILS':
                return '₪'
            case 'INR':
                return '₹'
            case 'JMD':
                return '$'
            case 'JPY':
                return '¥'
            case 'JOD':
                return 'د.ا'
            case 'KZT':
                return '〒'
            case 'KES':
                return 'KSh'
            case 'KWD':
                return 'د.ك'
            case 'KGS':
                return 'с'
            case 'LAK':
                return '₭'
            case 'LBP':
                return 'ل.ل'
            case 'LSL':
                return 'L'
            case 'LRD':
                return '$'
            case 'GBP':
                return '£'
            case 'MOP':
                return 'P'
            case 'MGA':
                return 'Ar'
            case 'MWK':
                return 'MK'
            case 'MYR':
                return 'RM'
            case 'MVR':
                return 'ރ.'
            case 'MUR':
                return '₨'
            case 'MXN':
                return '$'
            case 'MDL':
                return 'L'
            case 'MNT':
                return '₮'
            case 'MAD':
                return 'د.م.'
            case 'MZN':
                return 'MT'
            case 'MMK':
                return 'K'
            case 'NAD':
                return '$'
            case 'NPR':
                return '₨'
            case 'ANG':
                return 'ƒ'
            case 'NIO':
                return 'C$'
            case 'NGN':
                return '₦'
            case 'NOK':
                return 'kr'
            case 'OMR':
                return 'ر.ع.'
            case 'PKR':
                return '₨'
            case 'PAB':
                return 'B/.'
            case 'PGK':
                return 'K'
            case 'PYG':
                return '₲'
            case 'PEN':
                return 'S/.'
            case 'PHP':
                return '₱'
            case 'PLN':
                return 'zł'
            case 'QAR':
                return 'ر.ق'
            case 'RON':
                return 'lei'
            case 'RUB':
                return '₽'
            case 'RWF':
                return 'Fr'
            case 'SHP':
                return '£'
            case 'SVC':
                return '₡'
            case 'WST':
                return 'T'
            case 'SAR':
                return 'ر.س'
            case 'RSD':
                return 'дин.'
            case 'SCR':
                return '₨'
            case 'SLL':
                return 'Le'
            case 'SGD':
                return '$'
            case 'SBD':
                return '$'
            case 'SOS':
                return 'Sh'
            case 'ZAR':
                return 'R'
            case 'KRW':
                return '₩'
            case 'SSP':
                return '£'
            case 'LKR':
                return '₨'
            case 'SRD':
                return '$'
            case 'SZL':
                return 'E'
            case 'SEK':
                return 'kr'
            case 'CHF':
                return 'CHF'
            case 'SYP':
                return '£'
            case 'TWD':
                return 'NT$'
            case 'TZS':
                return 'Sh'
            case 'THB':
                return '฿'
            case 'TOP':
                return 'T$'
            case 'TTD':
                return '$'
            case 'TND':
                return 'د.ت'
            case 'TRY':
                return '₺'
            case 'TMT':
                return 'm'
            case 'UGX':
                return 'USh'
            case 'UAH':
                return '₴'
            case 'AED':
                return 'د.إ'
            case 'UYU':
                return '$'
            case 'UZS':
                return 'сум'
            case 'VUV':
                return 'Vt'
            case 'VES':
                return 'Bs.S'
            case 'VND':
                return '₫'
            case 'YER':
                return '﷼'
            case 'ZMW':
                return 'ZK'
            case 'GPB':
                return '£'
            default:
                return ''
        }
    }

    const currentCurrency = window?.Shopify?.currency?.active || 'USD'
    const currentCurrencySymbol = getSymbol(currentCurrency)
    var formatString = `${currentCurrencySymbol}{{amount}}`

    // Convert string to number if necessary
    /** @type {number} */
    let centsNumber
    if (typeof cents === 'string') {
        centsNumber = parseFloat(cents.replace('.', ''))
    } else {
        centsNumber = cents
    }

    var placeholderRegex = /\{\{\s*(\w+)\s*\}\}/

    if (centsNumber < 100) {
        return formatString.replace(placeholderRegex, formatWithDelimiters(centsNumber, 2))
    }

    return formatString.replace(placeholderRegex, formatWithDelimiters(centsNumber, 0))
}

function fx(thunk) {
    _fxQueue.push(thunk)
}

/**
 * gets product title for wishlist
 * @param {HTMLElement} el - the element to get the dt from
 * @returns {string} the dt
*/
function getDt(el) {
    return el?.getAttribute('data-dt')
}

/**
 * gets product url for wishlist
 * @param {HTMLElement} el - the element to get the du from
 * @returns {string} the du
*/
function getDu(el) {
    return el?.getAttribute('data-du')
}

/**
 * gets product id for wishlist
 * @param {HTMLElement} el - the element to get the empi from
 * @returns {number} the empi
*/
function getEmpi(el) {
    const empi = el?.getAttribute('data-empi')
    if (!empi) return 0
    return parseInt(empi)
}

/**
 * gets product variant id for wishlist
 * @param {HTMLElement} el - the element to get the epi from
 * @returns {number} the epi
*/
function getEpi(el) {
    const epi = el?.getAttribute('data-epi')
    if (!epi) return 0
    return parseInt(epi)
}

function getCprops(el) {
    const cprops = el?.getAttribute('data-cprops')
    if (!cprops) return {}
    return JSON.parse(cprops)
}

function getHeartsOf(el) {
    const emptyHeart = componentOf(el, 'empty-heart')
    const fullHeart = componentOf(el, 'full-heart')
    return { emptyHeart, fullHeart }
}

/** 
 * gets product image url for wishlist
 */
function getIu(el) {
    return el?.getAttribute('data-iu')
}

function getVariables(el) {
    return {
        empi: getEmpi(el),
        epi: getEpi(el),
        du: getDu(el),
        iu: getIu(el),
        dt: getDt(el),
        cprops: getCprops(el)
    }
}

/**
 * @param {Product} product - "molecule-product-tile"
 * @returns {string} image_zoom_class (e.g. "u-object-contain-blown-up--xl" or "")
 */
function getImageZoomClass(product) {
    if (!product || !Array.isArray(product.images)) return "";

    // find the first image whose alt contains "First"
    const selectedImage = product.media.find(media => {
        const alt = (media?.alt ?? "");
        const altUppercase = alt.toUpperCase()
        return typeof alt === "string" && altUppercase.includes("FIRST");
    })
    if (!selectedImage) return "";

    const tags = product.tags
    const productType = product.type
    const productTypeUppercase = productType.toUpperCase()

    /**
     * @param {string} t 
     * @returns {boolean}
     */
    const hasTag = (t) => tags.includes(t);

    let image_zoom_class = "";
    if (hasTag('zoom')) {
        image_zoom_class = CLASS_BLOW_UP_XXL;
    }
    else if (hasTag("zoom30")) {
        image_zoom_class = CLASS_BLOW_UP_XL;
    } else if (hasTag("zoom50")) {
        image_zoom_class = CLASS_BLOW_UP_XXL;
    } else if (hasTag("zoom70")) {
        image_zoom_class = CLASS_BLOW_UP_XXXL;
    }

    if (productTypeUppercase === "NECKLACE") {
        const isCuffBracelet = hasTag("cuff-bracelet");
        const isCollarNecklace = hasTag("collar-necklace");
        if (!isCuffBracelet && !isCollarNecklace) {
            image_zoom_class = CLASS_BLOW_UP_XL;
        }
    }

    return image_zoom_class;
}

function getVariant(epi, product) {
    const variant = product.variants.find(v => v.id === epi);
    return variant;
}

const getProductKey = (item) => item.empi || item.du || item.id

const getPersonalizedProductVariantId = (item) => {
    const variants = item.product?.variants ?? []
    const selectedValues = item.values ?? []

    const rateValue = selectedValues?.at(0)
    const colorValue = selectedValues?.at(1)
    const sizeValue = selectedValues?.at(2)

    const matchingVariant = variants.find(variant => {
        const variantRateValue = variant.options?.at(0)
        const variantColorValue = variant.options?.at(1)
        const variantSizeValue = variant.options?.at(2)

        return variantRateValue === rateValue && variantColorValue === colorValue && variantSizeValue === sizeValue
    })

    return matchingVariant?.id
}

/**
 * 
 * @param {Object} v 
 * @param {string} name 
 * @returns 
 */
function hasHook(v, name) {
    return v && v.props && typeof v.props[name] === 'function';
}

function condH(cond, ...nodes) {
    return cond ? nodes : [];
}

function h(type, props, ...children) {
    return hyperscript(type, props, ...children)
}

function hyperscript(type, props, ...children) {
    return { type, props: props || {}, children: children.flat() }
}

function isText(v) {
    return typeof v === 'string' || typeof v === 'number'
}

function isHtmlElement(v) {
    return v instanceof HTMLElement
}

const mapFunctions =
    (...functions) =>
        (...value) => {
            functions.forEach((fn) => fn(...value))
        }

const curriedDelegateOnClick = curriedDelegateListen('click')

function onDoc(callback) {
    return (event = 'DOMContentLoaded') => document.addEventListener(event, callback)
}

function runHook(name, v, node, oldProps) {
    if (!hasHook(v, name)) return;
    // schedule as a side-effect (post-paint) so layout is stable
    fx(() => v.props[name](node, { vnode: v, oldProps: oldProps || null }));
}

/**
 * Patches the DOM by diffing old and new virtual nodes
 * @param {Node} parent - The parent element
 * @param {Node|null} node - The current DOM node to patch
 * @param {VNode|string|number|null|undefined} oldV - The old virtual node
 * @param {VNode|string|number|null|undefined} newV - The new virtual node
 * @returns {Node|null} The patched node
 */
function patch(parent, node, oldV, newV) {
    // Insert
    if (!oldV) {
        parent.appendChild(createDom(newV));
        return parent.lastChild;
    }
    // Remove
    if (!newV) {
        // run onremove before detaching
        if (oldV && typeof oldV === 'object' && oldV.props) {
            runHook(HOOK_REMOVE, oldV, node, oldV.props);
        }
        parent.removeChild(node);
        return null;
    }
    // Text
    if (isText(oldV) || isText(newV)) {
        if (String(oldV) !== String(newV)) {
            // remove old, then create new (fire hooks appropriately)
            if (oldV && typeof oldV === 'object' && oldV.props) {
                runHook(HOOK_REMOVE, oldV, node, oldV.props);
            }
            const n = createDom(newV); // oncreate will fire inside
            parent.replaceChild(n, node);
            return n;
        }
        return node;
    }

    // At this point, both oldV and newV are VNode objects (not text)
    if (oldV.type !== newV.type) {
        // replacing different element types
        if (oldV && typeof oldV === 'object' && oldV.props) {
            runHook(HOOK_REMOVE, oldV, node, oldV.props);
        }
        const n = createDom(newV); // oncreate fires inside
        parent.replaceChild(n, node);
        return n;
    }

    if (!(node instanceof Element)) {
        console.error('Expected Element node for VNode patching');
        return node;
    }

    // same element type: update props/children
    setProps(node, newV.props, oldV.props);

    const oldKids = oldV.children || [];
    const newKids = newV.children || [];

    // --- keyed branch (with DOM node references) ---
    const oldKeyed = new Map();
    for (let i = 0; i < oldKids.length; i++) {
        const k = oldKids[i]?.props?.key;
        if (k !== undefined) oldKeyed.set(k, { v: oldKids[i], node: node.childNodes[i] });
    }

    if (oldKeyed.size > 0) {
        let cursor = 0;
        for (let i = 0; i < newKids.length; i++) {
            const nk = newKids[i]?.props?.key;
            if (nk !== undefined && oldKeyed.has(nk)) {
                const { v: ov, node: childNode } = oldKeyed.get(nk);
                const patched = patch(node, childNode, ov, newKids[i]);
                if (patched && patched !== node.childNodes[cursor]) {
                    node.insertBefore(patched, node.childNodes[cursor] || null);
                }
                oldKeyed.delete(nk);
            } else {
                const nChild = createDom(newKids[i]); // oncreate fires
                node.insertBefore(nChild, node.childNodes[cursor] || null);
            }
            cursor++;
        }
        // remove stale
        for (const { v: ov, node: staleNode } of oldKeyed.values()) {
            if (ov && typeof ov === 'object' && ov.props) {
                runHook(HOOK_REMOVE, ov, staleNode, ov.props);
            }
            if (staleNode && staleNode.parentNode === node) {
                node.removeChild(staleNode);
            }
        }
    } else {
        // --- unkeyed branch ---
        const len = Math.max(oldKids.length, newKids.length);
        for (let i = 0; i < len; i++) {
            const child = node.childNodes[i];
            const patched = patch(node, child, oldKids[i], newKids[i]);
            // no-op on append; remove handled by recursive branch
        }
    }

    // after children are reconciled, fire onupdate for this node
    if (newV && typeof newV === 'object' && newV.props) {
        runHook(HOOK_UPDATE, newV, node, oldV.props);
    }

    return node;
}

/**
 * Runs the function queue
 * @returns {void}
*/
function runFxQueue() {
    const q = _fxQueue;
    _fxQueue = [];
    for (const f of q) try { f(); } catch (e) { console.error(e); }
}

/**
 * 
 * @param {HTMLElement} el 
 * @param {string} className 
 * @returns 
 */
function removeClass(el, className) {
    el.classList.remove(className)
    return el
}

/**
 * Sets the src attribute of an image element
 * @param {HTMLImageElement} img - The image element
 * @param {string} src - The image source URL
 * @returns {HTMLImageElement} The image element with updated source
 */
function setSource(img, src) {
    img.setAttribute('src', src)
    return img
}

/**
 * Functional for-loop for hyperscript children.
 *
 * @template T
 * @param {Iterable<T> | null | undefined} items
 * @param {(item: T, index: number, all: T[]) => any} map   // return a VNode or array of VNodes
 * @param {{
*   key?: ((item: T, i: number) => string|number) | string, // auto-assign key prop (fn or property name)
*   empty?: any | (() => any),                              // fallback VNode(s) when no items
*   separator?: any | ((i: number, all: T[]) => any)        // between items (string | VNode | fn)
* }} [opts]
* @returns {any[]} an array to spread into hyperscript children
*/
function loopH(items, map, opts = {}) {
    const arr = items
        ? (Array.isArray(items) ? items : Array.from(items))
        : [];

    // Empty fallback
    if (arr.length === 0) {
        if (!('empty' in opts) || opts.empty == null) return [];
        const out = typeof opts.empty === 'function' ? opts.empty() : opts.empty;
        return Array.isArray(out) ? out : [out];
    }

    const needKey = !!opts.key;
    const getKey = typeof opts.key === 'function'
        ? opts.key
        : typeof opts.key === 'string'
            ? (() => {
                /** @type {string} */
                const keyProp = opts.key;
                return (item) => item?.[keyProp];
            })()
            : null;

    const withKey = (node, k) => {
        if (!node || typeof node !== 'object') return node;
        // don’t stomp existing keys
        if (node.props && node.props.key != null) return node;
        return {
            ...node,
            props: { ...(node.props || {}), key: String(k) }
        };
    };

    const result = [];

    for (let i = 0; i < arr.length; i++) {
        const item = arr[i];
        let node = map(item, i, arr);

        // mapper can return a VNode or an array of VNodes
        const nodes = Array.isArray(node) ? node : [node];

        if (needKey && getKey) {
            const k = getKey(item, i);
            if (k != null) {
                // Assign key to the first VNode of this item (common case is one VNode per item)
                if (nodes.length > 0 && typeof nodes[0] === 'object') {
                    nodes[0] = withKey(nodes[0], k);
                }
            }
        }

        result.push(...nodes);

        // separator between items
        if (i < arr.length - 1 && 'separator' in opts && opts.separator != null) {
            const sep = typeof opts.separator === 'function'
                ? opts.separator(i, arr)
                : opts.separator;
            if (sep != null) result.push(sep);
        }
    }

    return result;
}

/**
 * Schedules a function to run on the next animation frame with effect queue processing
 * @param {Function} next - The function to execute on next frame
 * @returns {void}
 */
function schedule(next) {
    if (_rafScheduled) return;
    _rafScheduled = true;
    requestAnimationFrame(() => { _rafScheduled = false; next(); runFxQueue(); });
}

/**
 * Sets the component state attribute on an element
 * @param {HTMLElement} el - The element to update
 * @param {string} state - The state value to set
 * @returns {HTMLElement} The element with updated state
 */
function setComponentState(el, state) {
    el.setAttribute('data-component-state', state)
    return el
}

/**
 * Sets component state to 'added'
 * @param {HTMLElement} el - The element to update
 * @returns {HTMLElement} The element with state set to added
 */
function setComponentStateToAdded(el) {
    return setComponentState(el, STATE_ADDED)
}

/**
 * Sets component state to 'not-added'
 * @param {HTMLElement} el - The element to update
 * @returns {HTMLElement} The element with state set to not-added
 */
function setComponentStateToNotAdded(el) {
    return setComponentState(el, STATE_NOT_ADDED)
}

/**
 * Sets properties on a DOM node, handling special cases for SVG and form elements
 * @param {Element} node - The DOM element to update (not text nodes)
 * @param {Object} [newProps={}] - The new properties to set
 * @param {Object} [oldProps={}] - The old properties to remove
 * @param {boolean} [isSvg] - Whether the element is SVG (defaults to checking namespaceURI)
 * @returns {void}
 */
function setProps(node, newProps = {}, oldProps = {}, isSvg = (node?.namespaceURI === SVG_NS)) {
    for (const k of Object.keys(oldProps)) {
        if (!(k in newProps)) {
            if (!isSvg && (k === 'value' || k === 'checked' || k === 'disabled')) node[k] = undefined;
            else node.removeAttribute(k);
        }
    }

    for (const [k, v] of Object.entries(newProps)) {
        if (v === oldProps[k]) continue;

        if (!isSvg && (k === 'value' || k === 'checked' || k === 'disabled')) {
            node[k] = v;
            continue;
        }

        if (typeof v === 'function') continue; // event/callback props ignored here

        // special-case xlink for SVG
        if (isSvg && (k === 'xlink:href' || k === 'xlinkHref')) {
            node.setAttributeNS(XLINK_NS, 'xlink:href', v);
        } else if (v === true) {
            node.setAttribute(k, '');
        } else if (v === false || v == null) {
            node.removeAttribute(k);
        } else {
            node.setAttribute(k, v);
        }
    }
}

/**
 * Shows the empty heart and hides the full heart
 * @param {HTMLElement} el - The parent element containing heart components
 * @returns {void}
 */
function showEmptyHeart(el) {
    const { emptyHeart, fullHeart } = getHeartsOf(el)

    if (validateObjectShallow({ emptyHeart, fullHeart })) {
        emptyHeart.classList.remove('u-hidden')
        fullHeart.classList.add('u-hidden')
    }
}

/**
 * Shows the empty heart and hides the full heart
 * @param {HTMLElement} el - The parent element containing heart components
 * @returns {void}
 */
function showFullHeart(el) {
    const { emptyHeart, fullHeart } = getHeartsOf(el)

    if (validateObjectShallow({ emptyHeart, fullHeart })) {
        emptyHeart.classList.add('u-hidden')
        fullHeart.classList.remove('u-hidden')
    }
}

/**
 * Validates that all values in an object are not null or undefined (shallow check)
 * @param {Record<string, any>} obj - The object to validate
 * @returns {boolean} True if all values are defined, false otherwise
 */
function validateObjectShallow(obj) {
    if (!obj) return false
    return Object.values(obj).every(value => value !== null && value !== undefined)
}

let _fxQueue = [];
let _rafScheduled = false;
// End utils

// Wishlist cards element creation
const WishlistGrid = (() => {
    let root = null;
    let model = { items: [], selectedColorIndex: new Map() };
    let oldV = null;
    let dom = null;

    // Methods
    const methods = {
        buildSelectedColorIndex(items) {
            const index = new Map()
            for (const it of items || []) {
                const key = getProductKey(it)
                const color = methods.getSelectedColor(it)
                if (!key || !color) continue
                if (!index.has(key)) index.set(key, new Set())
                index.get(key).add(color)
            }
            return index
        },
        filterColorsForCard(allColors, selectedColor, usedBySiblings, exception) {
            if (!Array.isArray(allColors) || allColors.length === 0) return []

            if (exception?.colorsToShow) {
                return [exception.colorsToShow]
            }

            const keep = []
            for (const c of allColors) {
                const lower = String(c || '').toLowerCase()
                if (!lower) continue

                const isUsedBySibling = usedBySiblings.has(lower)
                const isSelectedHere = lower === selectedColor
                if (isSelectedHere || !isUsedBySibling) keep.push(lower)
            }
            return keep
        },
        extractColorFromAlt: (alt) => {
            const match = colorsRegex.find(regex => regex.test(alt))
            const color = match?.source ?? ''
            return color
        },
        formatMaterialLabel: (item) => {
            const value = methods.getOptionOneValue(item)
            const valueUpcase = value.toUpperCase()
            if (valueUpcase == 'VERMEIL') {
                return MaterialLabel(TEXT_VERMIL)
            } else if (valueUpcase == 'GOLD VERMEIL') {
                return MaterialLabel(TEXT_VERMIL)
            } else if (valueUpcase == 'STERLING SILVER') {
                return MaterialLabel(TEXT_SILVER)
            } else if (valueUpcase == '14K') {
                return MaterialLabel(TEXT_GOLD_14K)
            } else if (valueUpcase == '18K') {
                return MaterialLabel(TEXT_GOLD_18K)
            } else if (valueUpcase == 'PLATINUM') {
                return MaterialLabel(TEXT_PLATINUM)
            } else {
                return MaterialLabel(value)
            }
        },
        filterColorMedia: (mediaArr) => {
            const imageMedia = mediaArr.filter(media => media?.media_type == 'image')
            const colorImages = imageMedia.filter(media => {
                const alt = media?.alt?.toLowerCase() ?? ''
                return alt.includes('first')
            })

            return colorImages
        },
        formatFinalPriceText: (item, epi) => {
            const variant = getVariant(epi, item)
            const variantPrice = variant.price
            const nextIndex = SALES_TIERS.findIndex(t => t.min > variantPrice)
            const saleTier = nextIndex > 0 ? SALES_TIERS[nextIndex - 1] : nextIndex === -1 ? SALES_TIERS[SALES_TIERS.length - 1] : null

            const showSaleCopy = window.showGlobalSale
            const saleRatio = saleTier?.ratio ?? 0
            const saleLabel = saleTier?.label ?? ''

            /** @type {PriceViewModel} */
            const priceModel = computePriceView({ product: item, variant, showSaleCopy: showSaleCopy, saleRatio: saleRatio, saleLabel: saleLabel })

            return priceElement(priceModel)
        },
        getOptionOneValue: (item) => {
            const variant = getVariant(item.epi, item)
            return variant.options[0]
        },
        getSelectedColor: (item) => {
            const epi = item.epi
            const selectedVariant = item.variants.find(variant => variant.id == epi)
            const selectedColor = selectedVariant?.option2?.toLowerCase() ?? ''
            return selectedColor
        }
    }

    const XIcon = (props = {}) =>
        h('svg', {
            style: "pointer-events: none;",
            xmlns: SVG_NS,
            viewBox: '0 0 24 24',
            width: '16',
            height: '16',
            fill: 'none',
            'aria-hidden': 'true',
            focusable: 'false',
            ...props
        },
            h('path', {
                d: 'M18 6L6 18M6 6l12 12',
                stroke: 'currentColor',
                'stroke-width': '2',
                'stroke-linecap': 'round',
                'stroke-linejoin': 'round'
            })
        )

    const Image = (props) => hyperscript('div', { class: `${CLASS_PRODUCT_TILE_MAIN_IMAGE_CONTAINER} ${props.isActive}`, [ATTRIBUTE_PRODUCT_TILE_IMAGE]: '' }, hyperscript('img', { src: props?.image?.src, alt: props?.image?.alt || '', class: `${getImageZoomClass(props.item)} u-object-contain-blown-up` }))

    const MainImages = (item) => {
        const coloredImages = methods.filterColorMedia(item.media)

        const fallbackImage = coloredImages[0]

        const selectedColor = methods.getSelectedColor(item)

        const activeImage = coloredImages.find(image => {
            const color = methods.extractColorFromAlt(image.alt)
            return color.toLowerCase() === selectedColor
        })

        const remainingImages = coloredImages.filter(image => {
            const color = methods.extractColorFromAlt(image.alt)
            return color.toLowerCase() !== selectedColor
        })

        return [
            hyperscript('div', { class: 'u-absolute u-inset-0 u-z-10' },
                Image({ item: item, image: (activeImage ?? fallbackImage), isActive: 'is-active' })
            ),
            ...loopH(remainingImages, (image) => {
                return Image({ item: item, image: image, isActive: '' })
            })
        ]
    }

    const HoverImage = (item) =>
        hyperscript('div', { class: `m-product-tile__overlay sm:u-block u-absolute u-inset-0 u-opacity-0 u-transition-all u-z-20`, 'data-product-tile-secondarily-image-container': '' },
            hyperscript('img', { src: item.images[item.images.length - 1], alt: item.dt || '', class: `u-object-cover-full` })
        )

    const MaterialLabel = (value) =>
        hyperscript('div', { class: 'u-flex u-flex-row u-justify-between' }, hyperscript('span', { class: 'p-text-eyebrow-2 u-mt-2 md:u-mt-1 u-mb-4' }, value))

    const Strike = (value) =>
        hyperscript('span', { class: 'u-whitespace-no-wrap u-line-through' }, value)

    const Discount = (value) =>
        hyperscript('span', { class: 'u-whitespace-no-wrap u-text-gold' }, value)

    const Nowrap = (value, doubled) =>
        hyperscript('span', { class: 'u-whitespace-no-wrap', ...(doubled ? { 'data-doubled': 'true' } : {}) }, value)

    const SaleLabel = (label) => {
        return hyperscript('span', { class: 'u-whitespace-no-wrap u-text-gold' },
            hyperscript('br', {}),
            label
        )
    }

    const colorSwatches = (item) => {
        const productId = item.id
        const colorImages = methods.filterColorMedia(item.media)

        const allColors = [...new Set(
            colorImages
                .map(img => methods.extractColorFromAlt(img.alt))
                .filter(Boolean)
                .map(c => String(c).toLowerCase())
        )]

        const selectedColor = methods.getSelectedColor(item)
        if (!allColors.length || !selectedColor) return []

        const key = getProductKey(item)
        const usedByProduct = model.selectedColorIndex.get(key) || new Set()

        const execeptionalProductOveride = productId === ID_MOTHERS_RING_PRODUCT ? {
            colorsToShow: selectedColor
        } : null

        const colorsToShow = methods.filterColorsForCard(allColors, selectedColor, usedByProduct, execeptionalProductOveride)

        if (colorsToShow.length === 0) return []

        const colorsWithSelected = colorsToShow.map(color => ({
            color,
            selected: color === selectedColor
        }))

        const button = (props) => {
            const selectionClassName = props.selected ? 'is-selected' : ''
            return hyperscript('button', {
                class: `a-swatch a-swatch--${props.color} u-mx-3 sm:u-mx-2 ${selectionClassName}`,
                'data-product-tile-swatch': props.color,
                'aria-label': `Select ${props.color} color`,
                'aria-pressed': props.selected,
                'aria-current': props.selected
            })
        }

        return hyperscript('div', { class: 'm-product-tile__swatches' },
            hyperscript('div', { class: 'u-flex u-items-center' },
                hyperscript('div', { class: 'u-relative u-flex u-z-10' }, ...loopH(colorsWithSelected, button))
            )
        )
    }

    /**
     * Render price content as hypertext (VNode) for your price element.
     * Expects the object produced by computePriceViewSimple().
     *
     * @param {Object} args
     * @param {'strikeDiscount'|'nowrap'} args.mode
     * @param {string|null} args.striked
     * @param {string|null} args.discounted
     * @param {string|null} args.text
     * @param {boolean} [args.doubled]
     * @param {string|null} [args.saleLabel]
     * @param {Object} [args.variantUsed]
     * @returns {Object} VNode
     */
    const priceElement = ({ discounted, doubled, mode, saleLabel, striked, text, variantUsed }) => {
        const containerAttrs = {
            class: 'm-price',
            ...(variantUsed?.id ? { 'data-variant-id': String(variantUsed.id) } : {})
        }

        if (mode === 'strikeDiscount') {
            return hyperscript('span', containerAttrs,
                Strike(striked || ''),
                ' ',
                Discount(discounted || ''),
                ...condH(!!saleLabel, SaleLabel(saleLabel))
            )
        }

        return hyperscript('span', containerAttrs,
            Nowrap(text || '', !!doubled),
            ...condH(!!saleLabel, SaleLabel(saleLabel))
        )
    }

    const Button = (item) => hyperscript('button', {
        class: CLASS_PRODUCT_CARD_DELETE,
        'data-msg': 'toggle-wishlist',
        'data-empi': item.empi,
        'data-epi': item.epi,
        'data-du': item.du,
        'data-iu': item.iu,
        'data-dt': item.dt,
        'aria-label': ARIA_REMOVE_FROM_WISHLIST,
        'data-component-wishlist-card-remove-button': '',
    }, XIcon())

    const Price = (item) => hyperscript('p', { class: 'p-text-eyebrow-2 sm:u-ml-8 sm:u-mt-1 u-text-gold-a sm:u-text-right' }, methods.formatFinalPriceText(item, item.epi))

    const Title = (item) => hyperscript('h3', { class: 'm-product-tile__title' }, item.title || '')

    /**
     * 
     * @param {WishlistProductInfo} item 
     * @returns 
     */
    const Link = (item) => {
        /** @type {wishlistCustomData} */
        const cprops = item.cprops

        /** @type {CompositeData} */
        const compositeData = cprops?.compositeData

        const baseUrl = compositeData?.baseProductUrl ?? item.du

        const url = new URL(baseUrl)

        url.searchParams.set('variant', `${item.epi}`)

        const hrefFinal = url.toString()

        return hyperscript('a', { href: hrefFinal || '#', class: 'm-product-tile__visual u-block u-relative u-aspect-ratio-17/22 u-bg-grey-warm u-z-0' },
            ...MainImages(item),
            HoverImage(item)
        )
    }

    // Pure card view (keys for stable patching)
    const card = (item) => hyperscript('article', {
        class: 'm-product-tile u-relative',
        'data-empi': item.empi,
        'data-epi': item.epi,
        'data-component-wishlist-card': '',
        'data-product-tile': '',
        key: String(item.epi)
    },
        Link(item),
        Button(item),
        hyperscript('div', { class: 'u-flex u-justify-between u-flex-col sm:u-flex-row u-mt-4' },
            Title(item),
            Price(item)
        ),
        methods.formatMaterialLabel(item),
        colorSwatches(item)
    );

    const emptyState = () =>
        hyperscript('div', { class: 'u-flex u-flex-col' },
            hyperscript('p', { class: 'u-mb-10' }, 'Your wishlist is empty.'),
            hyperscript('a', { class: 'a-button u-p-5', href: '/collections/all' }, 'START SHOPPING')
        )

    // Top level component
    const view = (m) =>
        h('div', {
            class: CLASS_PRODUCT_TILE_GRID,
            'data-collection-grid': '',
            [HOOK_CREATE]: emitGridUpdate,
            [HOOK_UPDATE]: emitGridUpdate
        },
            ...(m.items?.length ? m.items.map(card) : [emptyState()])
        )

    // Public API
    function mount(el) {
        root = el
        oldV = view(model)
        dom = createDom(oldV)
        root.innerHTML = ''
        root.appendChild(dom)
    }

    function setItems(items) {
        const nextItems = items || [];
        const selectedColorIndex = methods.buildSelectedColorIndex(nextItems);
        model = { ...model, items: nextItems, selectedColorIndex };

        if (!root) return;
        schedule(() => {
            const nextV = view(model)
            dom = patch(root, dom, oldV, nextV)
            oldV = nextV

            fx(() => {
                const sr = component(TEXT_SCREEN_READER_SELECTOR)
                if (sr) sr.textContent = `Wishlist items: ${model.items.length}`
            })
        })
    }

    function removeByEpi(productInfo) {
        const { empi, epi, du, iu, dt } = productInfo
        if (!root) return;


        const items = model.items.filter(it => {
            if (it.epi == epi) {
                return false
            }
            return true
        })

        model = { ...model, items: items };

        schedule(() => {
            const nextV = view(model);
            dom = patch(root, dom, oldV, nextV);
            oldV = nextV;
            fx(() => {
                const sr = component(TEXT_SCREEN_READER_SELECTOR);
                if (sr) sr.textContent = `Wishlist items: ${model.items.length}`;
            });
        });
    }

    return { mount, setItems, removeByEpi };
})()
// End wishlist cards element creation

// swym callbacks
async function swymCallbackOnWishlistFetchSuccess(lists) {
    if (!lists || lists.length === 0) {
        state.swat.createList(
            { lname: 'wishlist' }, // Must be an object with lname property
            () => {
                state.swat.fetchLists({
                    callbackFn: swymCallbackOnWishlistFetchSuccess,
                    errorFn: swymCallbackOnWishlistFetchError
                })
            },
            (error) => {
                console.error('Failed to create wishlist:', error)
                showErrorPopup('Failed to create wishlist: ' + JSON.stringify(error))
            }
        )
        return
    }

    emit(events.WISHLIST_LISTS_FETCH_SUCCESS, lists)
}

function swymCallbackOnWishlistFetchError(error) {
    console.error(error)
    showErrorPopup(JSON.stringify(error))
}

function swymCallbackOnWishlistItemDeletedSuccess(data) {
    emit(events.WISHLIST_ITEM_REMOVE_SUCCESS, data)
}

function swymCallbackOnWishlistItemDeletedError(error) {
    console.error(error)
    showErrorPopup(JSON.stringify(error))
}

function swymCallbackOnWishlistItemAddedSuccess(data) {
    emit(events.WISHLIST_ITEM_ADD_SUCCESS, data)
}

function swymCallbackOnWishlistItemAddedError(error) {
    console.error(error)
    showErrorPopup(JSON.stringify(error))
}

/// ACTIONS
const initializeWishlistGridContent = (el) => {
    if (el) {
        WishlistGrid.mount(el)
        WishlistGrid.setItems(state.productsInWishlist);
    }
}

// UI updates -- this is where the DOM is manipulated
const toggleScreenReaderText = (el, textOne, textTwo) => {
    const screenReaderText = componentOf(el, TEXT_SCREEN_READER_SELECTOR)
    if (screenReaderText) {
        const currentText = screenReaderText.textContent
        screenReaderText.textContent = currentText === textOne ? textTwo : textOne
    }
    return screenReaderText
}

const disableAllWishlistAddToggles = () => {
    components(SELECTOR_ADD_TO_WISHLIST_BUTTON).forEach(disableButton)
}

const updateWishlistLink = (el) => {
    const hasItems = state.firstList.listcontents.length > 0
    const loggedIn = el.getAttribute('data-logged-in') === 'true'

    if ((hasItems && loggedIn)) {
        el.href = URL_WISHLIST
        showFullHeart(el)
    } else {
        showEmptyHeart(el)
    }
}

const updateWishlistAddToggle = (el, e) => {
    const enable = () => {
        setComponentStateToAdded(el);
        showFullHeart(el);
        toggleScreenReaderText(el, TEXT_SCREEN_READER_ADD, TEXT_SCREEN_READER_REMOVE);
    }

    const disable = () => {
        setComponentStateToNotAdded(el);
        showEmptyHeart(el);
        toggleScreenReaderText(el, TEXT_SCREEN_READER_REMOVE, TEXT_SCREEN_READER_ADD);
    }

    // If no event detail provided (e.g., on init), just check current button state
    if (!e?.detail) {
        enableButton(el)
        const productInfo = getVariables(el)
        existsInList(state.firstList, productInfo) ? enable() : disable()
        return
    }

    if (e?.detail) {
        const detail = e.detail
        const imageSrc = detail?.selectedImageSrc
        const variantId = detail?.id
        const productId = detail?.product?.id
        const productTitle = detail?.product?.title

        /** @type {object} */
        const compositeData = detail?.compositeData
        /** @type {boolean} */
        const isComposite = detail?.isComposite
        /** @type {boolean} */
        const isToiEtMoi = detail?.compositeData?.isToiEtMoiProduct
        /** @type {boolean} */
        const isFourthVariantProduct = detail?.compositeData?.isFourthVariantProduct
        /** @type {boolean} */
        const isVanillaProduct = !isComposite && !isToiEtMoi
        /** @type {boolean} */
        const isFourthOptionProduct = isComposite && isFourthVariantProduct && !isToiEtMoi
        /** @type {boolean} */
        const isToiEtMoiProduct = isComposite && isToiEtMoi
        /** @type {boolean} */
        const isPersonalizedProduct = detail?.compositeData?.isPersonalizedProduct

        const setVanillaProductProductInfo = ({ productTitle, productId, variantId, imageSrc }) => {
            if (productTitle) {
                el.dataset.dt = productTitle
            }

            if (productId) {
                el.dataset.empi = productId
            }

            if (variantId) {
                el.dataset.epi = variantId
            }

            if (imageSrc) {
                el.dataset.iu = imageSrc
            }

            enableButton(el)

            const productInfo =  getVariables(el)

            existsInList(state.firstList, productInfo) ? enable() : disable();
        }

        const setFourthOptionProductInfo = ({  imageSrc, compositeData }) => {
            const productHandle = compositeData?.fourthVariantProductHandle
            const url = `${URL_ROOT}products/${productHandle}.json`
            fetch(url).then(response => response.json()).then(response => {
                return response.product
            }).then(fetchedProduct => {
                if (fetchedProduct.title) {
                    el.dataset.dt = fetchedProduct.title
                } else {
                    console.warn('productTitle is missing')
                }
                if (fetchedProduct.id) {
                    el.dataset.empi = fetchedProduct.id
                } else {
                    console.warn('productId is missing')
                }

                if (compositeData?.passedVariantId) {
                    el.dataset.epi = compositeData?.passedVariantId
                } else {
                    console.warn('compositeData.passedVariantId is missing')
                }

                if(url) {
                    el.dataset.du = `${URL_ROOT}products/${productHandle}`
                }

                if (imageSrc || (fetchedProduct?.images?.length > 0 && fetchedProduct.images[0].src) ) {
                    el.dataset.iu = imageSrc || fetchedProduct.images[0].src
                } else {
                    console.warn('imageSrc is missing')
                }

                if (compositeData) {
                    try {
                        const compositeDataString = JSON.stringify(compositeData)
                        el.dataset.cprops = compositeDataString
                    } catch (error) {
                        console.error(error)
                        showErrorPopup(error)
                    }

                    enableButton(el)

                    const productInfo =  getVariables(el)

                    existsInList(state.firstList, productInfo) ? enable() : disable()
                } else {
                    console.warn('compositeData is missing')
                }
            })
        }

        const setToiEtMoiProductProductInfo = (item, imageSrc, compositeData) => {
            if(item.product.title){
                el.dataset.dt = item?.product?.title ?? ''
            }

            if (item.product.id){
                el.dataset.empi = item?.product?.id ?? 0
            }

            if(item.id) {
                el.dataset.epi = item?.id ?? 0
            }

            if(imageSrc) {
                el.dataset.iu = imageSrc
            }

            if(compositeData) {
                try {
                    const compositeDataString = JSON.stringify(compositeData)
                    el.dataset.cprops = compositeDataString
                } catch (error) {
                    console.error(error)
                    showErrorPopup(error)
                }
            }

            enableButton(el)

            const productInfo =  getVariables(el)

            existsInList(state.firstList, productInfo) ? enable() : disable()
        }

        const setPersonalizedProductProductInfo = (item, imageSrc, compositeData) => {
            if(item.product.title){
                el.dataset.dt = item?.product?.title ?? ''
            }

            if (item.product.id){
                el.dataset.empi = item?.product?.id ?? 0
            }

            if(item.id) {
                el.dataset.epi = getPersonalizedProductVariantId(item) ?? 0
            }

            if(imageSrc) {
                el.dataset.iu = imageSrc
            }

            if(compositeData) {
                try {
                    const compositeDataString = JSON.stringify(compositeData)
                    el.dataset.cprops = compositeDataString
                } catch (error) {
                    console.error(error)
                    showErrorPopup(error)
                }
            }

            enableButton(el)

            const productInfo =  getVariables(el)

            existsInList(state.firstList, productInfo) ? enable() : disable()
        }

        if (isFourthOptionProduct) {
            setFourthOptionProductInfo({ imageSrc, compositeData })
        } else if (isVanillaProduct) {
            setVanillaProductProductInfo({ productTitle, productId, variantId, imageSrc })
        } else if(isToiEtMoiProduct) {

            setToiEtMoiProductProductInfo(detail, imageSrc, compositeData)
        } else if (isPersonalizedProduct) {
            setPersonalizedProductProductInfo(detail, imageSrc, compositeData)
        }
    }
}

const setWishlistAddToggleToNotAdded = (el) => {
    enableButton(el)

    const productInfo = getVariables(el)
    const exists = existsInList(state.firstList, productInfo)
    if (exists) {

        setComponentStateToNotAdded(el)
        showEmptyHeart(el)
        toggleScreenReaderText(el, TEXT_SCREEN_READER_REMOVE, TEXT_SCREEN_READER_ADD)
        emit(events.WISHLIST_LISTS_FETCH_REQUEST, state.swat)
    }
}

const setWishlistAddToggleToAdded = (el) => {
    enableButton(el)

    const productInfo = getVariables(el)
    const exists = existsInList(state.firstList, productInfo)

    if (!exists) {

        setComponentStateToAdded(el)
        showFullHeart(el)
        toggleScreenReaderText(el, TEXT_SCREEN_READER_ADD, TEXT_SCREEN_READER_REMOVE)
        emit(events.WISHLIST_LISTS_FETCH_REQUEST, state.swat)
    }
}

const displayWishlistItemRemovedPopup = (popup, e) => {
    const productImage = e.detail.iu
    const wishlistItemAddedPopup = component(SELECTOR_WISHLIST_ADDED_POPUP)

    const imageElement = componentOf(popup, SELECTOR_WISHLIST_NOTIFICATION_IMAGE)

    if (!(imageElement instanceof HTMLImageElement)) {
        console.error('Image element is not an instance of HTMLImageElement')
        return
    }

    imageElement && setSource(imageElement, productImage)

    if (isHtmlElement(popup)) removeClass(popup, CLASS_HIDDEN)

    setTimeout(() => addClass(popup, CLASS_HIDDEN), TIMEOUT_POPUP)

    wishlistItemAddedPopup && addClass(wishlistItemAddedPopup, CLASS_HIDDEN)
}

const removeWishlistItemFromGrid = (_, e) => {
    const productInfo = e.detail

    WishlistGrid.removeByEpi(productInfo)
}

const displayWishlistItemAddedPopup = (popup, e) => {
    const wishlistItemRemovedPopup = component(SELECTOR_WISHLIST_REMOVED_POPUP)

    const productTitle = e.detail.dt
    const productImage = e.detail.iu

    const titleElement = componentOf(popup, SELECTOR_WISHLIST_NOTIFICATION_TITLE)
    /* @type {HTMLImageElement} */
    const imageElement = componentOf(popup, SELECTOR_WISHLIST_NOTIFICATION_IMAGE)

    if (!(imageElement instanceof HTMLImageElement)) {
        console.error('Image element is not an instance of HTMLImageElement')
        return
    }

    if (titleElement && imageElement) {
        titleElement.textContent = productTitle
        setSource(imageElement, productImage)
    }

    if (isHtmlElement(popup)) removeClass(popup, CLASS_HIDDEN)

    setTimeout(() => addClass(popup, CLASS_HIDDEN), TIMEOUT_POPUP)

    wishlistItemRemovedPopup && addClass(wishlistItemRemovedPopup, CLASS_HIDDEN)
}

/**
 * Updates the wishlist grid with fresh data from the API after an item is added
 * @param {HTMLElement} _ - Unused element parameter
 * @param {CustomEvent} e - The event containing added item details
 * @returns {void}
 */
function updateWishlistGridOnAdd(_, e) {
    if (!state.swat) return;
    state.swat.fetchLists({
        callbackFn: async (lists) => {
            const firstList = lists[0]
            const firstItems = firstList.listcontents

            const products = firstItems.map(async item => {
                const productInfo = await fetchProductInfo(item.du)
                return { ...item, ...productInfo }
            })

            state.productsInWishlist = await Promise.all(products)

            const grid = component(SELECTOR_WISHLIST_GRID)
            if (grid) {
                WishlistGrid.setItems(state.productsInWishlist)
            }
        },
        errorFn: swymCallbackOnWishlistFetchError
    })
}

// on events
const onWishlistInit = curriedListen(
    events.WISHLIST_INIT
)

const onWishlistItemDeletedSuccess = curriedListen(
    events.WISHLIST_ITEM_REMOVE_SUCCESS
)

const onWishlistItemAddedSuccess = curriedListen(
    events.WISHLIST_ITEM_ADD_SUCCESS
)

const onVariantChange = curriedListen(
    events.VARIANT.CHANGE
)

const onWishlistLoad = (e) => {
    const swat = e.detail
    state.swat = swat

    emit(events.WISHLIST_LISTS_FETCH_REQUEST, swat)
}

const onWishlistFetchSuccess = async (e) => {
    const lists = e.detail
    const swat = state.swat

    const firstList = lists[0]
    state.firstList = firstList

    const firstItems = firstList.listcontents

    const products = firstItems.map(async item => {
        const productInfo = await fetchProductInfo(item.du)
        return { ...item, ...productInfo }
    })

    state.productsInWishlist = await Promise.all(products)

    emit(events.WISHLIST_INIT, swat)
}

const onWishlistItemRemoveRequest = (e) => {
    const productInfo = e.detail

    disableAllWishlistAddToggles()
    disableAllWishlistAddToggles()

    state.swat.deleteFromList(state.firstList.lid, productInfo, swymCallbackOnWishlistItemDeletedSuccess, swymCallbackOnWishlistItemDeletedError)
}

const onWishlistItemAddRequest = (e) => {
    const productInfo = e.detail

    disableAllWishlistAddToggles()

    state?.swat.addToList(state.firstList.lid, productInfo, swymCallbackOnWishlistItemAddedSuccess, swymCallbackOnWishlistItemAddedError)
}

const onWishlistFetchRequest = (e) => {
    const swat = e.detail
    state.swat = swat

    state.swat.fetchLists({
        callbackFn: swymCallbackOnWishlistFetchSuccess,
        errorFn: swymCallbackOnWishlistFetchError
    })
}

// end actions


/// EVENT COMPOSITION

// emits
const emitWishlistToggle = (button) => {
    const productInfo = getVariables(button)
    const exists = existsInList(state.firstList, productInfo)

    disableAllWishlistAddToggles()

    if (exists) {
        emit(events.WISHLIST_REMOVE_REQUEST, productInfo)
    } else {
        emit(events.WISHLIST_ADD_REQUEST, productInfo)
    }
}

const emitWishlistRemoveRequest = (button) => {
    const productInfo = getVariables(button)
    emit(events.WISHLIST_REMOVE_REQUEST, productInfo)
}

// handlers
const wishlistLoadHandler = onDoc(onWishlistLoad)

const wishlistFetchRequestHandler = onDoc(onWishlistFetchRequest)

const wishlistFetchSuccessHandler = onDoc(onWishlistFetchSuccess)

const wishlistItemRemoveRequestHandler = onDoc(onWishlistItemRemoveRequest)

const wishlistItemAddRequestHandler = onDoc(onWishlistItemAddRequest)

const wishlistItemDeletedSuccessHandler = onDoc(onWishlistItemDeletedSuccess)

const wishlistLinkHandler = mapFunctions(
    onWishlistInit(updateWishlistLink),
    onWishlistItemDeletedSuccess(updateWishlistLink),
    onWishlistItemAddedSuccess(updateWishlistLink),
)

const wishlistAddToggleHandler = mapFunctions(
    onWishlistInit(updateWishlistAddToggle),
    onWishlistItemDeletedSuccess(setWishlistAddToggleToNotAdded),
    onWishlistItemAddedSuccess(setWishlistAddToggleToAdded),
    onVariantChange(updateWishlistAddToggle)

)

const wishlistAddedPopupHandler = mapFunctions(
    onWishlistItemAddedSuccess(displayWishlistItemAddedPopup)
)

const wishlistRemovedPopupHandler = mapFunctions(
    onWishlistItemDeletedSuccess(displayWishlistItemRemovedPopup)
)

const wishlistGridHandler = mapFunctions(
    onWishlistItemAddedSuccess(updateWishlistGridOnAdd),
    onWishlistItemDeletedSuccess(removeWishlistItemFromGrid),
    onWishlistInit(initializeWishlistGridContent)
)

const wishlistAddClickHandler = curriedDelegateOnClick(`${DATA_COMPONENT_PREFIX}-${SELECTOR_ADD_TO_WISHLIST_BUTTON}`)

const wishlistItemRemoveClickHandler = curriedDelegateOnClick(`${DATA_COMPONENT_PREFIX}-${SELECTOR_WISHLIST_CARD_REMOVE_BUTTON}`)


// end event composition

// Event listener attachment
wishlistLoadHandler(events.WISHLIST_LOAD)
wishlistFetchRequestHandler(events.WISHLIST_LISTS_FETCH_REQUEST)
wishlistFetchSuccessHandler(events.WISHLIST_LISTS_FETCH_SUCCESS)
wishlistItemRemoveRequestHandler(events.WISHLIST_REMOVE_REQUEST)
wishlistItemAddRequestHandler(events.WISHLIST_ADD_REQUEST)
wishlistItemDeletedSuccessHandler(events.WISHLIST_ITEM_REMOVE_SUCCESS)

// Final function
const wishListMainFunc = swat => {
    emit(events.WISHLIST_LOAD, swat)

    wishlistLinkHandler(component(SELECTOR_WISHLIST_LINK))

    components(SELECTOR_WISHLIST_LINK).forEach(link => wishlistLinkHandler(link))

    components(SELECTOR_ADD_TO_WISHLIST_BUTTON).forEach(button => wishlistAddToggleHandler(button))

    wishlistAddClickHandler((button) => emitWishlistToggle(button))

    wishlistAddedPopupHandler(component(SELECTOR_WISHLIST_ADDED_POPUP))
    wishlistRemovedPopupHandler(component(SELECTOR_WISHLIST_REMOVED_POPUP))

    wishlistGridHandler(component(SELECTOR_WISHLIST_GRID))

    wishlistItemRemoveClickHandler((button) => emitWishlistRemoveRequest(button))
}

if (!window.SwymCallbacks) {
    window.SwymCallbacks = []
}

window.SwymCallbacks.push(wishListMainFunc)